import React, { useState, useEffect } from 'react';
import FeedBackCard from '../FeedbackCard';
import RatingCard from '../RatingCard';
import NotFoundCard from '../NotFoundCard'
import * as Vlab from 'vlab-frontend-components';
import { useTranslation } from 'react-i18next';

interface TabItem {
  title: string;
  id: string;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  total: number;
  detailsLevel?: string;
}

interface FeedbackRatingTabProps {
  feedbacks: any[];
  ratings: any[];
  ratingsAverage: number | null;
  pagination: PaginationProps;
  setPagination: (values: PaginationProps) => void;
  activeTab?: string;
  onContentTypeChange?: (contentType: string) => void;
  containerClassName?: string;
  isLoading?: boolean;
}

function FeedbackRatingTabs({ 
  feedbacks, 
  ratings, 
  ratingsAverage,
  pagination, 
  setPagination, 
  activeTab = 'ratings',
  onContentTypeChange, 
  containerClassName,
  isLoading = false
}: FeedbackRatingTabProps) {
  const { t } = useTranslation();

  const tabItems: TabItem[] = [
    { title: t('components.tabs.ratings'), id: 'ratings' },
    { title: t('components.tabs.feedbacks'), id: 'feedbacks' }
  ];

  const currentTab = tabItems.find(tab => tab.id === activeTab) || tabItems[0];

  const handleTabChange = (tab: TabItem) => {
    if (tab.id !== activeTab && !isLoading) {
      if (onContentTypeChange) {
        onContentTypeChange(tab.id);
      }
    }
  };

  const handlePageChange = (page: number) => {
    if (isLoading) return;
    setPagination({
      ...pagination,
      currentPage: page - 1,
      detailsLevel: activeTab
    });
  };

  const currentPage = pagination.currentPage;
  const totalPages = pagination.totalPages || 1;

  return (
    <div className={`w-full ${containerClassName}`}>
      <div className="flex flex-col items-start p-6 bg-[#F7F8FC] w-full rounded flex-1 self-stretch h-full max-h-[380px] relative">
        <div className="flex w-full border-b border-[#DDE5EC] absolute left-0 top-0 sm:static">
          <div className="flex w-full sm:w-auto justify-between sm:justify-start">
            {tabItems.map((tab, index) => (
              <div
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className={`pb-2 cursor-pointer ${
                  index === 0 ? 'sm:mr-6' : ''
                } font-semibold text-[18px] leading-[18px] relative ${
                  currentTab.id === tab.id 
                    ? 'text-[#0166FF]' 
                    : 'text-[#B7C6D1]'
                } flex-1 sm:flex-none text-center sm:text-left py-6 pt-4 sm:pt-0 pb-4 sm:pb-2`}
                style={{
                  userSelect: 'none',
                }}
              >
                {tab.title}      
                
                {currentTab.id === tab.id && (
                  <div 
                    className="absolute h-[3px] bg-[#0166FF] rounded-[30px] bottom-[-1.5px] left-0 w-full sm:left-1/2 sm:w-auto sm:transform sm:-translate-x-1/2"
                    style={{ width: 'calc(100%)', maxWidth: 'none' }}
                  />
                )}
              </div>
            ))}
          </div>
          
          <div className="hidden sm:flex items-center gap-2 ml-auto">
            <button 
              onClick={() => {
                if (currentPage > 0) {
                  handlePageChange(currentPage);
                }
              }}
              disabled={currentPage === 0 || isLoading}
              className={`flex items-center justify-center w-[18px] h-[18px] cursor-pointer ${
                currentPage === 0 ? 'text-gray-400' : 'text-[#0166FF]'
              }`}
            >
              <Vlab.ArrowLeftIcon width="16px" height="16px" />
            </button>
            
            <span className="text-[12px] text-[#51525F] leading-[120%] text-center">
              {t('components.pagination.page')} {currentPage + 1} {t('components.pagination.of')} {totalPages}
            </span>
            
            <button 
              onClick={() => {
                if (currentPage < (totalPages - 1)) {
                  handlePageChange(currentPage + 2);
                }
              }}
              disabled={currentPage >= (totalPages - 1) || isLoading}
              className={`flex items-center justify-center w-[18px] h-[18px] cursor-pointer ${
                currentPage >= (totalPages - 1) ? 'text-gray-400' : 'text-[#0166FF]'
              }`}
            >
              <Vlab.ArrowRightIcon width="16px" height="16px" />
            </button>
          </div>
        </div>
  
        <div className="w-full h-[calc(100%-40px)] mt-12 sm:mt-4 overflow-x-auto lg:overflow-y-auto lg:overflow-x-hidden hide-scrollbar-mobile">
          {isLoading ? (
            <div className="flex items-center justify-center h-full animate-pulse">
              <div className="w-full">
                <div className="h-16 bg-gray-200 rounded-md mb-3"></div>
                <div className="h-16 bg-gray-200 rounded-md mb-3"></div>
                <div className="h-16 bg-gray-200 rounded-md"></div>
              </div>
            </div>          
          ) : currentTab.id === 'feedbacks' ? (
            feedbacks && feedbacks.length > 0 ? (
              <div className="flex flex-col h-full">
                <FeedBackCard 
                  feedbacks={feedbacks} 
                  containerClassName="pl-0 pt-0 bg-transparent h-full max-h-none" 
                />
              </div>
            ) : (
              <div className="p-6 bg-[#F7F8FC] w-full rounded text-[12px] h-full">
                <div className="mt-4">
                  <NotFoundCard />
                </div>
              </div>
            )
          ) : (
            ratings && ratings.length > 0 ? (
              <div className="flex flex-col h-full">
                <RatingCard 
                  ratings={ratings} 
                  average={ratingsAverage as number} 
                  containerClassName="pl-0 pt-0 bg-transparent h-[calc(100%-60px)] max-h-none" 
                />
              </div>
            ) : (
              <div className="p-6 bg-[#F7F8FC] w-full rounded text-[12px] h-full">
                <div className="mt-4">
                  <NotFoundCard />
                </div>
              </div>
            )
          )}
        </div>

        <div className="sm:hidden flex items-center gap-2 justify-center w-full">
          <button 
            onClick={() => {
              if (currentPage > 0) {
                handlePageChange(currentPage);
              }
            }}
            disabled={currentPage === 0 || isLoading}
            className={`flex items-center justify-center w-[30px] h-[30px] cursor-pointer ${
              currentPage === 0 ? 'text-gray-400' : 'text-[#0166FF]'
            }`}
          >
            <Vlab.ArrowLeftIcon width="16px" height="16px" />
          </button>
          
          <span className="text-[14px] text-[#51525F] leading-[120%] text-center min-w-[90px] font-medium">
            {t('components.pagination.page')} {currentPage + 1} {t('components.pagination.of')} {totalPages}
          </span>
          
          <button 
            onClick={() => {
              if (currentPage < (totalPages - 1)) {
                handlePageChange(currentPage + 2);
              }
            }}
            disabled={currentPage >= (totalPages - 1)}
            className={`flex items-center justify-center w-[30px] h-[30px] cursor-pointer ${
              currentPage >= (totalPages - 1) ? 'text-gray-400' : 'text-[#0166FF]'
            }`}
          >
            <Vlab.ArrowRightIcon width="16px" height="16px" />
          </button>
        </div>

      </div>
    </div>
  );
}

export default FeedbackRatingTabs;